import { Alert, CircularProgress } from "@mui/material";
import * as React from "react";
import { Link, Navigate, useMatch, useParams } from "react-router";

import {
  ExpandableCopyText,
  PageBreadcrumbs,
  PageLayout,
} from "@/shared/components";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { useSession } from "@/shared/domain/sessions/hooks";
import { userFacingErrorMessage } from "@/shared/http";
import { Goto, SessionTab } from "@/shared/navigation";
import { useRegisterViewingContext } from "@/shared/services/ViewingContextService";

import { SessionChrome } from "./SessionChrome";
import styles from "./SessionDetail.module.css";
import { SessionLayout } from "./SessionLayout";

/**
 * Detail page for a Session at ``/sessions/:sessionId``.
 *
 * ``SessionDetailShell`` renders the title and breadcrumb across all
 * three render branches:
 *
 * 1. **Loading** — shows the breadcrumb and a spinner body.
 * 2. **Success** — the resolved ``SessionRecord`` flows to the body
 *    (``SessionLayout``) from a single ``useSession`` call, so a fetch
 *    failure raises exactly one ``ErrorBoundary``.
 * 3. **Error** — the body swaps to ``SessionErrorFallback``; the
 *    breadcrumb, tab strip, and back-link stay reachable.
 *
 * 4XX bodies surface verbatim (the backend shapes them for users);
 * everything else falls back to generic copy via
 * :func:`userFacingErrorMessage`.
 */
export function SessionDetail() {
  const { sessionId } = useParams<{ sessionId: string }>();

  // Publish the session to AI Chat so the agent can resolve "this session"
  // without the user pasting its id. Registered from the URL id, so it holds
  // while the record loads or fails; the loaded record adds its `orgId` below.
  useRegisterViewingContext("session", { sessionId });

  if (sessionId === undefined) {
    // Programming error — the route declaration registers
    // `/sessions/:sessionId` so React Router guarantees the param is
    // set by the time this component mounts.
    throw new Error("sessionId not provided");
  }

  return (
    <ErrorBoundary
      fallback={({ error }) => (
        <SessionDetailShell sessionId={sessionId}>
          <SessionErrorFallback error={error} sessionId={sessionId} />
        </SessionDetailShell>
      )}
    >
      <React.Suspense
        fallback={
          <SessionDetailShell sessionId={sessionId}>
            <div className={styles.loadingContainer}>
              <CircularProgress size="1.5rem" />
            </div>
          </SessionDetailShell>
        }
      >
        <SessionDetailLoaded sessionId={sessionId} />
      </React.Suspense>
    </ErrorBoundary>
  );
}

/**
 * Sole ``useSession`` call site. The resolved record flows by prop to
 * ``SessionLayout`` so the page has one cache subscription and one
 * suspense throw, keeping loading and error transitions consolidated
 * in the body while the shell stays stable.
 */
function SessionDetailLoaded({ sessionId }: { sessionId: string }) {
  const { data: session } = useSession({ sessionId });
  // The session's org drives the top bar's "View as" switch: a session is
  // read by its id alone, so a system user can open one owned by another org.
  useRegisterViewingContext("session", {
    sessionId,
    record: session,
    orgId: session.org_id,
  });

  return (
    <SessionDetailShell sessionId={sessionId}>
      <SessionLayout session={session} />
    </SessionDetailShell>
  );
}

/**
 * Page chrome shared by every render branch. Renders ``PageLayout``
 * with the title-bar breadcrumb and accepts the body as children, so
 * the breadcrumb and document title stay stable across loading,
 * success, and error states.
 */
function SessionDetailShell({
  sessionId,
  children,
}: {
  sessionId: string;
  children: React.ReactNode;
}) {
  return (
    <PageLayout
      title={`Roboto - Session - ${sessionId}`}
      headerContentLeft={
        <PageBreadcrumbs>
          <Link to={Goto.route.Sessions}>Sessions</Link>
          <ExpandableCopyText text={sessionId} trimLength={36} />
        </PageBreadcrumbs>
      }
    >
      {children}
    </PageLayout>
  );
}

interface SessionErrorFallbackProps {
  sessionId: string;
  error: Error;
}

/**
 * Body shown when the session fails to load: the shared ``SessionChrome``
 * with no record (the heading shows the session id from the URL and the
 * metadata row is omitted), then the error message in an alert.
 *
 * A URL that stops at the session id is normally sent to the Topics tab by
 * the index route, which renders inside ``SessionLayout``'s ``<Outlet>``.
 * This fallback replaces ``SessionLayout``, so it redirects that URL itself;
 * without that the URL would disagree with the tab strip, which shows Topics
 * as active.
 */
function SessionErrorFallback({ sessionId, error }: SessionErrorFallbackProps) {
  const isBareSessionUrl =
    useMatch(`${Goto.route.Sessions}/:sessionId`) !== null;

  return (
    <div className={styles.root}>
      {isBareSessionUrl ? (
        <Navigate
          to={Goto.createSessionRoute(sessionId, SessionTab.Topics)}
          replace
        />
      ) : null}
      <SessionChrome sessionId={sessionId} session={null} />
      <div className={styles.tabPanel}>
        <Alert severity="error">
          {userFacingErrorMessage(
            error,
            <>
              Failed to load session. Please contact{" "}
              <a href="mailto:support@roboto.ai">support@roboto.ai</a> if this
              problem persists.
            </>,
          )}
        </Alert>
      </div>
    </div>
  );
}
